import { Event, useStore } from '@mirai/data-sources';
import { currencySymbol, useLocale } from '@mirai/locale';
import { Action, Button, Modal, ScrollView, styles, useDevice, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { InputRange } from './components';
import { Amenity } from './Filters.Amenity';
import { BookingOption } from './Filters.BookingOption';
import { Button as ButtonFilters } from './Filters.Button';
import { DEFAULT_FORM, MAX_BOARDS } from './Filters.constants';
import { L10N } from './Filters.l10n';
import * as style from './Filters.module.css';
import { countFilters, getAvailableFilters, getBookingOptions, getStrictSearch } from './helpers';
import { EVENT, ICON, METRICS_EVENTS } from '../../../helpers';
import { fetchRates, select } from '../../helpers';
import { Option } from '../Item/components';

let currentCurrency;
let currentPlace;
let memoizeForm;

const Filters = ({
  clubDiscount,
  items: propItems = [],
  priceFactor = 1,
  values,
  onOpen = () => {},
  onSubmit = () => {},
  ...others
}) => {
  const { isMobile } = useDevice();
  const { translate } = useLocale();
  const {
    value: {
      currency,
      club = {},
      finder = {},
      hotel,
      id,
      locale,
      occupation: { maxRooms } = {},
      session,
      urlParams: { idoffers, roomTypeId } = {},
    } = {},
  } = useStore();

  const { place, promocode } = finder;

  const [allAmenities, setAllAmenities] = useState(false);
  const [busy, setBusy] = useState(false);
  const [counter, setCounter] = useState(0);
  const [filters, setFilters] = useState(getAvailableFilters(propItems, { priceFactor }));
  const [form, setForm] = useState(DEFAULT_FORM);
  const [items, setItems] = useState(propItems);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const nextForm = memoizeForm || {
      ...DEFAULT_FORM,
      offer: idoffers ? !!idoffers : undefined,
      promocode: promocode ? !!promocode : undefined,
      roomType: roomTypeId ? !!roomTypeId : undefined,
    };

    setForm(nextForm);
    setCounter(countFilters(nextForm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.priceRange || (currentCurrency !== undefined && currentCurrency === currency)) return;

    currentCurrency = currency;
    const nextForm = { ...form, priceRange: undefined };

    setForm(nextForm);
    setCounter(countFilters(nextForm));
    onSubmit({ ...nextForm, strictSearch: getStrictSearch(nextForm) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  useEffect(() => {
    if (!place || (currentPlace !== undefined && JSON.stringify(currentPlace) === JSON.stringify(place))) return;

    if (currentPlace !== undefined) {
      setForm(DEFAULT_FORM);
      setCounter(countFilters(DEFAULT_FORM));
      onSubmit(DEFAULT_FORM);
    }
    currentPlace = place;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place]);

  useEffect(() => {
    const setNextState = (nextForm) => {
      setForm(nextForm);
      setCounter(countFilters(nextForm));
    };

    const setClubPromocode = ({ promocode }) => setNextState({ ...form, promocode });

    Event.subscribe(EVENT.RATES_FILTER_PROMOCODE, setClubPromocode);

    return () => Event.unsubscribe(EVENT.RATES_FILTER_PROMOCODE, setClubPromocode);
  }, [form]);

  useEffect(() => {
    if (JSON.stringify(propItems) !== JSON.stringify(items)) setItems(propItems);

    const nextFilters = getAvailableFilters(propItems, { priceFactor });
    if (JSON.stringify(nextFilters) !== JSON.stringify(filters)) setFilters(nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propItems]);

  useEffect(() => {
    if (!values || Object.keys(values).length === 0) return;

    if (JSON.stringify(values) !== JSON.stringify(form)) {
      setForm(values);
      setCounter(countFilters(values));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const handleModal = () => {
    const next = !visible;

    setVisible(next);
    if (next) {
      onOpen();
    }
  };

  const handleAmenity = (id, value) => {
    setForm({ ...form, amenities: value ? [...form.amenities, id] : form.amenities.filter((item) => item !== id) });
  };

  const handleOption = (field, id) => {
    setForm({ ...form, [field]: form[field] === id ? undefined : id });
  };

  const handleRange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleBookingOption = async (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);

    if (field !== 'freeCancellation') await fetch(next);
  };

  const handleClearAll = () => {
    const { offer, promocode, roomType } = form || {};
    setAllAmenities(false);
    setForm({ ...DEFAULT_FORM });

    (offer || promocode || roomType) && fetch(DEFAULT_FORM);
  };

  const handleSubmit = () => {
    memoizeForm = form;
    setVisible(false);
    const nextCounter = countFilters(form);
    setCounter(nextCounter);
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.RATES_INTERACTION,
      action: 'filter_applied',
      filter_used_cancellation: !!form.freeCancellation,
      filter_used_board: !!form.board,
      filter_used_price: !!form.priceRange,
      filter_used_amenities: form.amenities?.length > 0,
      filter_used_bed: !!form.bedType,
      filter_used_size: !!form.sizeRange,
    });
    onSubmit({ ...form, strictSearch: getStrictSearch(form) });
  };

  const fetch = async (next) => {
    setBusy(true);

    const strictSearch = getStrictSearch(next);
    const cart = []; // ! TODO: [MF-2006] Store should get the current cart of <Rates>
    const params = { cart, club, clubDiscount, finder, hotel, id, locale, maxRooms, session, strictSearch };
    const { items: nextItems } = (await fetchRates(params).catch(() => {})) || {};
    setItems(nextItems);
    if (nextItems) setFilters(getAvailableFilters(nextItems, { priceFactor }));

    setBusy(false);
  };

  const has = {
    freeCancellation: true || !!filters.freeCancellation, // * We'll activate freeCancellation filter always.
    offer: !!idoffers,
    promocode: !!promocode,
    roomType: !!roomTypeId,
  };

  const filteredItems = select(items, form, { priceFactor }).filter(({ availability = 0 } = {}) => availability > 0);
  const { length: boardsLength } = filters.boards;
  const inlineBoards = !isMobile || (isMobile && boardsLength <= MAX_BOARDS - 1);
  const { name: roomTypeName } = items?.find(({ id } = {}) => id === parseInt(roomTypeId)) || {};
  const { name: offerName } =
    (has.offer &&
      items?.map(({ boards = [], rates = [] } = {}) => {
        const { rateId } = boards.find(({ deals = [] } = {}) => deals.includes(idoffers.split(',')[0])) || {};

        return rates.find(({ id }) => id === rateId);
      }))?.[0] || {};

  return (
    <>
      <ButtonFilters {...{ counter }} onPress={handleModal} />

      <Modal
        fit
        portal
        title={translate(L10N.LABEL_FILTERS)}
        visible={visible}
        onClose={handleModal}
        className={[style.modal, others.className]}
      >
        <ScrollView scrollIndicator scrollTo={visible ? 0.5 : undefined} snap={false} className={style.scrollView}>
          <View className={style.section}>
            <Text bold headline>
              {translate(L10N.LABEL_BOOKING_OPTIONS)}
            </Text>

            {getBookingOptions(!!idoffers && idoffers.split(',').length === 1)
              .filter(({ field } = {}) => has[field])
              .map(({ field, title } = {}) => (
                <BookingOption
                  key={field}
                  checked={form[field]}
                  {...{ field, title, offerName, roomName: roomTypeName, promocode: promocode?.toUpperCase() }}
                  onChange={handleBookingOption}
                />
              ))}
          </View>

          {boardsLength > 1 && (
            <View className={style.section}>
              <Text bold headline>
                {translate(L10N.LABEL_ACCOMMODATION_PLAN)}
              </Text>

              <View row={inlineBoards} className={styles(style.boards, inlineBoards && style.inline)}>
                {filters.boards.map(({ id, text, type }) => (
                  <Option
                    checked={form.board === type}
                    key={`board:${id}`}
                    inline={inlineBoards}
                    outlined
                    title={text}
                    onPress={() => handleOption('board', type)}
                    className={style.option}
                  />
                ))}
              </View>
            </View>
          )}

          {filters.prices.length > 1 && (
            <InputRange
              caption={translate(priceFactor === 1 ? L10N.LABEL_SET_PRICE_RANGE : L10N.LABEL_NIGHTLY_PRICES)}
              dataSource={filters.prices}
              resolution={isMobile || filters.prices.length <= 8 ? 6 : 10}
              title={translate(L10N.LABEL_PRICE_RANGE)}
              unit={currencySymbol(currency, { locale })}
              value={form.priceRange}
              onChange={(value) => handleRange('priceRange', value)}
              className={style.section}
            />
          )}

          <View className={style.section}>
            <Text bold headline wide>
              {translate(L10N.LABEL_AMENITIES)}
            </Text>

            <View>
              <View row className={style.amenities}>
                {filters.amenities
                  .filter((item) => item.highlight)
                  .map(({ id, text }) => (
                    <Amenity
                      checked={form.amenities.includes(id)}
                      id={id}
                      key={id}
                      label={text}
                      onChange={handleAmenity}
                    />
                  ))}
              </View>

              {allAmenities ? (
                <>
                  <Text action bold wide className={style.textOthers}>
                    {translate(L10N.LABEL_OTHERS)}
                  </Text>

                  <View row className={style.amenities}>
                    {filters.amenities
                      .filter((item) => !item.highlight)
                      .map(({ id, text }) => (
                        <Amenity
                          checked={form.amenities.includes(id)}
                          id={id}
                          key={id}
                          label={text}
                          onChange={handleAmenity}
                        />
                      ))}
                  </View>
                </>
              ) : (
                <Action onPress={() => setAllAmenities(true)}>{translate(L10N.ACTION_VIEW_MORE)}</Action>
              )}
            </View>
          </View>

          {filters.bedTypes.length > 1 && (
            <View className={style.section}>
              <Text bold headline wide>
                {translate(L10N.LABEL_BED_TYPE)}
              </Text>

              <View row>
                {filters.bedTypes.map((bedType) => (
                  <Option
                    key={`bedType:${bedType}`}
                    checked={form.bedType === bedType}
                    icon={bedType === 1 ? ICON.BED_SINGLE : ICON.BED_DOUBLE}
                    inline
                    outlined
                    title={translate(bedType === 1 ? L10N.LABEL_BED_TYPE_SINGLE : L10N.LABEL_BED_TYPE_DOUBLE)}
                    onPress={() => handleOption('bedType', bedType)}
                    className={style.option}
                  />
                ))}
              </View>
            </View>
          )}

          {filters.sizes.length > 1 && (
            <InputRange
              dataSource={filters.sizes}
              resolution={isMobile ? 4 : 6}
              title={translate(L10N.LABEL_ROOM_SIZE)}
              unit={filters.sizeUnit}
              value={form.sizeRange}
              onChange={(value) => handleRange('sizeRange', value)}
              className={style.section}
            />
          )}
        </ScrollView>

        <View row className={[style.footer, style.spaceBetween]}>
          <Button transparent onPress={handleClearAll}>
            {translate(L10N.ACTION_CLEAR_ALL)}
          </Button>
          <Button busy={busy} disabled={!filteredItems.length} onPress={handleSubmit}>
            {translate(filteredItems.length ? L10N.LABEL_SHOW_COUNT_RESULTS : L10N.LABEL_NO_EXACT_MATCHES, {
              count: filteredItems.length,
            })}
          </Button>
        </View>
      </Modal>
    </>
  );
};

Filters.displayName = 'Mirai:Core:Rates:Filters';

Filters.propTypes = {
  clubDiscount: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  priceFactor: PropTypes.number,
  values: PropTypes.shape({}),
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func,
};

export { Filters };
